import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import KeycardPanel from "@/components/KeycardPanel";
import Topbar from "@/components/layout/Topbar";
import { useProperties } from "@/contexts/PropertyContext";
import { StatusBadge } from "@/components/StatusBadge";
import { formatCurrency, formatDate, addDays, toYMD, nightsBetween, parseYMDLocal } from "@/lib/format";
import { ChevronLeft, ChevronRight, ChevronDown, Plus, X, Calendar as CalIcon, Globe2, User, CreditCard, FileText, Moon, BedDouble, Receipt, Wrench, Ban, ExternalLink, Tag, CalendarSearch, UsersRound, Send, Home, CalendarDays } from "lucide-react";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import useRealtime from "@/lib/realtime";
import GuestPicker, { toReservationGuestPayload, isGuestPickerValid } from "@/components/reservations/GuestPicker";

const DAY_WIDTH = typeof window !== "undefined" && window.innerWidth < 768 ? 72 : 110;
const CATEGORY_COL_WIDTH = 0;    // legacy — kept as 0 so absolute positioning math continues to work; category is now a horizontal header row above each group
const ROOM_COL_WIDTH = typeof window !== "undefined" && window.innerWidth < 768 ? 120 : 168;
const ROW_HEIGHT = 48;
const SECTION_HEADER_HEIGHT = 56;  // horizontal category header with availability + rate per date
const HEADER_ROW_HEIGHT = 0;   // removed — categories now render as horizontal header rows
const GRID_HEADER_HEIGHT = 92;  // top weekday/date row — large pill style
const MONTH_BAND_HEIGHT = 24;   // sticky month strip above the date row — bars must clear it

const WEEKDAY_SQ = ["Die", "Hën", "Mar", "Mër", "Enj", "Pre", "Sht"]; // Sunday-first to match Date.getDay()

function formatWeekdayShort(d, lang) {
    if ((lang || "").startsWith("sq")) return WEEKDAY_SQ[d.getDay()];
    return d.toLocaleDateString("en-GB", { weekday: "short" });
}

const ROOM_STATUS_META = {
    clean:        { bg: "#dcfce7", color: "#15803d", dot: "#22c55e", label: "clean" },
    dirty:        { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b", label: "dirty" },
    occupied:     { bg: "#dbeafe", color: "#1046a4", dot: "#1046a4", label: "occupied" },
    maintenance:  { bg: "#fee2e2", color: "#b91c1c", dot: "#dc2626", label: "maintenance" },
    blocked:      { bg: "#e8ebf0", color: "#475569", dot: "#64748b", label: "blocked" },
    out_of_order: { bg: "#fee2e2", color: "#991b1b", dot: "#7f1d1d", label: "out of order" },
};

function RoomStatusPill({ status }) {
    const { t } = useTranslation();
    const m = ROOM_STATUS_META[status] || { bg: "#e8ebf0", color: "#475569", dot: "#94a3b8", label: status || "—" };
    // "clean" is the default/idle state → show ONLY the dot (no text) to
    // keep the calendar clean; other statuses show a compact pill with the
    // localised label so operators know at a glance what needs attention.
    if (status === "clean") {
        return (
            <span
                className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: m.dot }}
                title={t("rooms.status.clean", { defaultValue: "e pastër" })}
                data-testid="room-status-clean"
            />
        );
    }
    const label = status ? t(`rooms.status.${status}`, { defaultValue: m.label }) : "—";
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ background: m.bg, color: m.color }}
        >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.dot }} />
            {label}
        </span>
    );
}

const STATUS_COLOR = {
    confirmed: "#2563EB",     // blue — booked, not yet arrived
    checked_in: "#059669",    // emerald — in-house guest
    checked_out: "#94A3B8",   // slate — departed
    pending: "#F59E0B",       // amber — awaiting confirmation
    cancelled: "#DC2626",     // red — cancelled
    no_show: "#7C3AED",       // violet — no-show (distinct from cancelled)
};

// Diagonal-stripe overlay for statuses that need extra visual distinction
// even at a glance (cancelled and no_show).
const STATUS_STRIPES = new Set(["cancelled", "no_show"]);

const SOURCE_LABEL = {
    direct: "Direkt",
    booking: "Booking.com",
    airbnb: "Airbnb",
    expedia: "Expedia",
    ical: "iCal",
};

// Per-source visual chip used on the reservation bar (and expanded in the
// tooltip). Uses initials on a coloured square so each channel is
// instantly recognisable. Colors mirror each brand's primary palette.
const SOURCE_BADGE = {
    direct:  { text: "D",  bg: "#ffffff", fg: "#1e3c72", ring: "#e8ebf0" },
    booking: { text: "B.", bg: "#003580", fg: "#ffffff", ring: "#ffffff33" },
    airbnb:  { text: "A",  bg: "#FF385C", fg: "#ffffff", ring: "#ffffff33" },
    expedia: { text: "E",  bg: "#FDB92A", fg: "#0F1B4A", ring: "#ffffff33" },
    ical:    { text: "iC", bg: "#64748b", fg: "#ffffff", ring: "#ffffff33" },
};

/**
 * Per-channel branded chip drawn to the RIGHT of the guest name.
 * Recognisable via brand color + brand mark (initial for OTAs,
 * lucide glyph for generic sources).
 */
function SourceChip({ source, title }) {
    const src = source || "direct";
    const cls = "inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-[3px] text-[8.5px] font-black leading-none ring-1 ring-white/40";
    const _title = title || SOURCE_LABEL[src] || src;
    switch (src) {
        case "booking":
            return <span className={cls} style={{ background: "#003580", color: "#fff", letterSpacing: "-0.05em" }} title={_title}>B.</span>;
        case "airbnb":
            return <span className={cls} style={{ background: "#FF385C", color: "#fff" }} title={_title}>A</span>;
        case "expedia":
            return <span className={cls} style={{ background: "#FDB92A", color: "#0F1B4A" }} title={_title}>E</span>;
        case "ical":
            return <span className={cls} style={{ background: "#64748b", color: "#fff" }} title={_title}><CalendarDays size={9} strokeWidth={2.5} /></span>;
        case "direct":
        default:
            return <span className={cls} style={{ background: "rgba(255,255,255,0.94)", color: "#1e3c72" }} title={_title}><Home size={9} strokeWidth={2.5} /></span>;
    }
}

// Reservation-bar clip-path — modern PMS "parallelogram tape" look. Both
// edges slant in the SAME direction (forward `/____/`) so the whole bar
// reads as a forward-moving stay, and adjacent bars in the tape chart
// interlock cleanly without butted verticals.
const TICKET_CLIP =
    "polygon(12px 0, 100% 0, calc(100% - 12px) 100%, 0 100%)";

// Room-block visual meta (operational maintenance / owner use, etc.)
// Rendered as diagonal-striped bars in a slate colour so they never
// look like reservations.
const BLOCK_META = {
    maintenance:  { label: "Mirëmbajtje",       color: "#64748b", icon: Wrench },
    out_of_order: { label: "Jashtë përdorimi",   color: "#7f1d1d", icon: Ban },
    owner_use:    { label: "Përdorim personal",  color: "#4b5563", icon: User },
    renovation:   { label: "Rinovim",            color: "#0f172a", icon: Wrench },
    deep_clean:   { label: "Pastrim i thellë",   color: "#0369a1", icon: Wrench },
    other:        { label: "Tjetër",             color: "#475569", icon: Ban },
};

// Roles allowed to open the Rate popover / edit rates / manage blocks.
const RATE_MANAGE_ROLES = new Set(["super_admin", "hotel_admin", "property_manager"]);

export default function Reservations() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Auto-open the "new reservation" drawer when the URL has `?new=1`
    // (e.g. from Dashboard's "Rezervim i ri" CTA button).
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("new") === "1") {
            setSelected(null);
            setOpenDrawer(true);
            // Clean the query param so refreshing the page doesn't re-open the modal.
            const url = new URL(window.location.href);
            url.searchParams.delete("new");
            window.history.replaceState({}, "", url.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
    const canManageRates = RATE_MANAGE_ROLES.has(user?.role);
    const { properties, activePropertyId } = useProperties();
    const [rooms, setRooms] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [roomBlocks, setRoomBlocks] = useState([]);
    // Room-type × date summary from the aggregated backend endpoint.
    // Keyed by `${room_type_name}|${YYYY-MM-DD}`.
    const [summaryByKey, setSummaryByKey] = useState({});
    const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - 3); return d; });
    const days = 14;
    const [propertyId, setPropertyId] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [openGroupDrawer, setOpenGroupDrawer] = useState(false);
    const [bulkBlockOpen, setBulkBlockOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    // Rate popover state — anchored to a clicked summary cell.
    // { rt_id, rt_name, date, x, y } | null
    const [ratePopover, setRatePopover] = useState(null);
    // Block modal state — create/edit a room block.
    // null | { mode: "create" | "edit", block, defaultRoomId?, defaultDate? }
    const [blockModal, setBlockModal] = useState(null);
    const [dragState, setDragState] = useState(null); // {mode, resId, startX, startCI, startCO, startRoomId, hoverRoomId, deltaDays}
    const gridRef = useRef(null);

    // Collapsed category sections — persisted per-user across reloads.
    const [collapsed, setCollapsed] = useState(() => {
        try {
            const raw = localStorage.getItem("akm.reservations.collapsedSections");
            return new Set(raw ? JSON.parse(raw) : []);
        } catch { return new Set(); }
    });
    const toggleSection = useCallback((key) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            try { localStorage.setItem("akm.reservations.collapsedSections", JSON.stringify([...next])); } catch {/* ignore */}
            return next;
        });
    }, []);

    useEffect(() => {
        if (activePropertyId !== "all") setPropertyId(activePropertyId);
        else if (properties.length > 0) setPropertyId(properties[0].id);
    }, [properties, activePropertyId]);

    const load = useCallback(async () => {
        if (!propertyId) return;
        const start = toYMD(anchor);
        const end = toYMD(addDays(anchor, days));
        try {
            const [r, res, sum] = await Promise.all([
                api.get(`/rooms?property_id=${propertyId}`),
                api.get(`/reservations?property_id=${propertyId}&from_date=${start}&to_date=${end}`),
                api.get(`/reservations/calendar-summary?property_id=${propertyId}&from=${start}&to=${end}`).catch(() => ({ data: { rows: [], room_blocks: [] } })),
            ]);
            setRooms(r.data);
            setReservations(res.data);
            const map = {};
            for (const row of (sum.data?.rows || [])) {
                map[`${row.room_type_name || ""}|${row.date}`] = row;
            }
            setSummaryByKey(map);
            setRoomBlocks(sum.data?.room_blocks || []);
        } catch (e) {
            console.error("Failed to load reservations", e);
            toast.error("Could not load reservations. Please refresh.");
            setRooms([]);
            setReservations([]);
            setSummaryByKey({});
            setRoomBlocks([]);
        }
    }, [propertyId, anchor]);

    useEffect(() => { load(); }, [load]);

    // Realtime: refresh when other clients change reservations on this hotel
    useRealtime((msg) => {
        if (!msg?.event) return;
        if (
            msg.event.startsWith("reservation.") ||
            msg.event === "room.status_changed" ||
            msg.event.startsWith("room_block.")
        ) {
            load();
        }
    });

    const dateRange = useMemo(() => Array.from({ length: days }, (_, i) => addDays(anchor, i)), [anchor]);

    // Room-status quick filter for the receptionist (top-right dropdown).
    // Empty string ("") = show all rooms. Otherwise filters `rooms` before
    // grouping into category sections.
    const [statusFilter, setStatusFilter] = useState("");

    // Sort rooms by room_type then number — keeps groups contiguous
    const sortedRooms = useMemo(() => {
        const src = statusFilter ? rooms.filter((r) => r.status === statusFilter) : rooms;
        return [...src].sort((a, b) => {
            const t = (a.room_type || "").localeCompare(b.room_type || "");
            if (t !== 0) return t;
            return (a.number || "").toString().localeCompare((b.number || "").toString(), undefined, { numeric: true });
        });
    }, [rooms, statusFilter]);

    const roomIndex = useMemo(() => {
        const m = {}; sortedRooms.forEach((r, i) => { m[r.id] = i; }); return m;
    }, [sortedRooms]);

    // Group sortedRooms into category sections + pixel-Y map for bar overlay.
    // Each expanded section has a horizontal HEADER row (SECTION_HEADER_HEIGHT)
    // above its rooms. Collapsed sections show only that header (compact).
    const COLLAPSED_ROW = SECTION_HEADER_HEIGHT;
    const layout = useMemo(() => {
        const sections = [];
        const yTop = {};
        let cursor = 0;
        let current = null;
        for (const r of sortedRooms) {
            const key = r.room_type || "—";
            if (!current || current.key !== key) {
                if (current) sections.push(current);
                current = {
                    key, label: key, rooms: [],
                    color: r.room_type_color || null,
                    startY: cursor,
                    collapsed: collapsed.has(key),
                };
            }
            current.rooms.push(r);
        }
        if (current) sections.push(current);
        // Compute each section's absolute Y start + total height.
        let cy = 0;
        for (const s of sections) {
            s.startY = cy;
            s.headerY = cy;                                   // horizontal header row lives here
            if (s.collapsed) {
                s.heightPx = SECTION_HEADER_HEIGHT;
                s.rooms.forEach((r) => { yTop[r.id] = null; });
            } else {
                s.heightPx = SECTION_HEADER_HEIGHT + s.rooms.length * ROW_HEIGHT;
                s.rooms.forEach((r, i) => {
                    yTop[r.id] = cy + SECTION_HEADER_HEIGHT + i * ROW_HEIGHT;
                });
            }
            cy += s.heightPx;
        }
        return { sections, yTop, totalHeight: cy };
    }, [sortedRooms, collapsed]);

    const byRoom = useMemo(() => {
        const map = {};
        for (const r of reservations) (map[r.room_id] = map[r.room_id] || []).push(r);
        return map;
    }, [reservations]);

    // ----- DnD logic -----
    const startDrag = (e, mode, res) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState({
            mode, resId: res.id,
            startX: e.clientX, startY: e.clientY,
            startCI: res.check_in, startCO: res.check_out, startRoomId: res.room_id,
            hoverRoomId: res.room_id,
            deltaDays: 0,
            deltaRows: 0,
        });
    };

    useEffect(() => {
        if (!dragState) return;
        const onMove = (e) => {
            const dx = e.clientX - dragState.startX;
            const dy = e.clientY - dragState.startY;
            const deltaDays = Math.round(dx / DAY_WIDTH);
            const deltaRows = Math.round(dy / ROW_HEIGHT);
            const startIdx = roomIndex[dragState.startRoomId] ?? 0;
            const targetIdx = Math.max(0, Math.min(sortedRooms.length - 1, startIdx + deltaRows));
            const hoverRoomId = sortedRooms[targetIdx]?.id || dragState.startRoomId;
            setDragState((s) => s && { ...s, deltaDays, deltaRows, hoverRoomId });
        };
        const onUp = async () => {
            const ds = dragState;
            setDragState(null);
            if (!ds) return;
            if (ds.deltaDays === 0 && ds.deltaRows === 0) return;
            let newCI = ds.startCI, newCO = ds.startCO, newRoom = ds.hoverRoomId;
            const ci = new Date(ds.startCI);
            const co = new Date(ds.startCO);
            if (ds.mode === "move") {
                ci.setDate(ci.getDate() + ds.deltaDays);
                co.setDate(co.getDate() + ds.deltaDays);
            } else if (ds.mode === "resize-start") {
                ci.setDate(ci.getDate() + ds.deltaDays);
                newRoom = ds.startRoomId; // room unchanged on resize
            } else if (ds.mode === "resize-end") {
                co.setDate(co.getDate() + ds.deltaDays);
                newRoom = ds.startRoomId;
            }
            newCI = toYMD(ci);
            newCO = toYMD(co);
            if (newCO <= newCI) {
                toast.error(t("reservations.checkout_must_be_after"));
                return;
            }
            try {
                await api.patch(`/reservations/${ds.resId}`, { check_in: newCI, check_out: newCO, room_id: newRoom });
                await load();
                toast.success(t("reservations.updated_success"));
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("reservations.save_failed"));
                await load();
            }
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [dragState, roomIndex, sortedRooms, load, t]);

    return (
        <>
            <Topbar
                title={t("reservations.title")}
                subtitle={`${properties.find((p) => p.id === propertyId)?.name || "—"} · ${formatDate(anchor)} → ${formatDate(addDays(anchor, days - 1))}`}
                actions={
                    <>
                        <button
                            data-testid="new-bulk-block-btn"
                            onClick={() => setBulkBlockOpen(true)}
                            className="akm-btn-ghost gap-1 border border-border"
                            title="Blloko shumë dhoma (mirëmbajtje / mbyllje sezonale)"
                        >
                            <Ban size={14} /> Blloko shumë
                        </button>
                        <button
                            data-testid="new-group-reservation-btn"
                            onClick={() => setOpenGroupDrawer(true)}
                            className="akm-btn-ghost gap-1 border border-border"
                            title="Rezervim grupi (shumë dhoma, faturë e vetme)"
                        >
                            <UsersRound size={14} /> Rezervim grup
                        </button>
                        <button data-testid="new-reservation-btn" onClick={() => { setSelected(null); setOpenDrawer(true); }} className="akm-btn-primary">
                            <Plus size={14} /> {t("reservations.new")}
                        </button>
                    </>
                }
            />
            <div className="flex flex-1 min-h-0 flex-col overflow-auto p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                    <button onClick={() => setAnchor(addDays(anchor, -7))} className="akm-btn-ghost px-2" data-testid="cal-prev"><ChevronLeft size={14} /></button>
                    <button onClick={() => setAnchor(() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - 3); return d; })} className="akm-btn-ghost" data-testid="cal-today"><CalIcon size={14} /> {t("reservations.today")}</button>
                    <button onClick={() => setAnchor(addDays(anchor, 7))} className="akm-btn-ghost px-2" data-testid="cal-next"><ChevronRight size={14} /></button>
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                className="akm-btn-ghost gap-1.5"
                                data-testid="cal-goto-date"
                                title="Shko te datë specifike"
                            >
                                <CalendarSearch size={13} /> Shko te data
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <CalendarPicker
                                mode="single"
                                selected={anchor}
                                onSelect={(d) => {
                                    if (!d) return;
                                    const dt = new Date(d);
                                    dt.setHours(0, 0, 0, 0);
                                    // Center the picked date: anchor = date-3 so the picked day
                                    // sits in the visible band (matches the "Sot" behavior).
                                    dt.setDate(dt.getDate() - 3);
                                    setAnchor(dt);
                                }}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                    <div className="ml-2 text-[11px] text-muted-foreground">{t("reservations.rooms_count", { rooms: rooms.length, reservations: reservations.length })}</div>
                    <div className="ml-auto flex items-center gap-2">
                        <RoomStatusFilter rooms={rooms} value={statusFilter} onChange={setStatusFilter} />
                        {properties.length > 1 && (
                            <select value={propertyId || ""} onChange={(e) => setPropertyId(e.target.value)} className="akm-input h-8 w-[200px] text-[13px]" data-testid="cal-property">
                                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        )}
                    </div>
                </div>

                <div className="akm-card relative overflow-auto" style={{ height: "calc(100vh - 210px)", minHeight: 320 }} ref={gridRef}>
                    <div className="relative" style={{ minWidth: ROOM_COL_WIDTH + days * DAY_WIDTH }}>
                        {/* Today vertical highlight — spans the full grid body, sits BEHIND section rows */}
                        {(() => {
                            const idx = dateRange.findIndex((d) => toYMD(d) === toYMD(new Date()));
                            if (idx < 0) return null;
                            return (
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute z-[0]"
                                    style={{
                                        left: ROOM_COL_WIDTH + idx * DAY_WIDTH,
                                        top: GRID_HEADER_HEIGHT + 24,
                                        width: DAY_WIDTH,
                                        height: layout.totalHeight,
                                        background: "linear-gradient(180deg, rgba(16,70,164,0.06), rgba(16,70,164,0.02))",
                                        borderLeft: "1px dashed rgba(16,70,164,0.35)",
                                        borderRight: "1px dashed rgba(16,70,164,0.35)",
                                    }}
                                    data-testid="today-vertical-line"
                                />
                            );
                        })()}
                        {/* Month bands — sticky-top strip that names the month(s) covering the visible range */}
                        {(() => {
                            const bands = [];
                            const sqMonths = ["Janar","Shkurt","Mars","Prill","Maj","Qershor","Korrik","Gusht","Shtator","Tetor","Nëntor","Dhjetor"];
                            const enMonths = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                            let start = 0;
                            for (let i = 1; i <= dateRange.length; i++) {
                                const prev = dateRange[i - 1];
                                const cur = dateRange[i];
                                if (!cur || cur.getMonth() !== prev.getMonth() || cur.getFullYear() !== prev.getFullYear()) {
                                    const isSq = (i18n.language || "").startsWith("sq");
                                    const monthNames = isSq ? sqMonths : enMonths;
                                    bands.push({
                                        span: i - start,
                                        label: `${monthNames[prev.getMonth()]} ${prev.getFullYear()}`.toUpperCase(),
                                    });
                                    start = i;
                                }
                            }
                            return (
                                <div className="sticky top-0 z-[11] grid border-b border-border bg-card/95 backdrop-blur"
                                     style={{ gridTemplateColumns: `${ROOM_COL_WIDTH}px repeat(${dateRange.length}, ${DAY_WIDTH}px)`, height: 24 }}>
                                    <div className="sticky left-0 z-[1] border-r border-border bg-card" />
                                    {bands.map((b, idx) => (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-center border-l border-[#e8ebf0] text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                                            style={{ gridColumn: `span ${b.span} / span ${b.span}` }}
                                        >
                                            {b.label}
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                        {/* Header */}
                        <div className="sticky z-10 grid border-b border-border bg-card"
                             style={{ top: 24, gridTemplateColumns: `${ROOM_COL_WIDTH}px repeat(${days}, ${DAY_WIDTH}px)`, height: GRID_HEADER_HEIGHT }}>
                            <div className="sticky left-0 z-10 flex items-end justify-start border-r border-border bg-card px-3 pb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("reservations.room")}</div>
                            {dateRange.map((d) => {
                                const isToday = toYMD(d) === toYMD(new Date());
                                const isSunday = d.getDay() === 0;
                                const isWeekend = isSunday || d.getDay() === 6;
                                const wd = formatWeekdayShort(d, i18n.language);
                                const numColor = isToday ? "#1046a4" : isSunday ? "#dc2626" : "#1e3c72";
                                return (
                                    <div key={toYMD(d)} className="flex items-center justify-center py-2">
                                        <div
                                            className={`flex h-[70px] w-[58px] flex-col items-center justify-center rounded-[18px] border ${
                                                isToday ? "border-[#1046a4] shadow-sm bg-[#e8ebf0]" : isWeekend ? "border-[#cbd5e1] bg-[#f8fafc]" : "border-[#e8ebf0] bg-card"
                                            }`}
                                        >
                                            <div className="text-[28px] font-light leading-none tracking-tight" style={{ color: numColor }}>
                                                {d.getDate()}
                                            </div>
                                            <div className="mt-1 text-[10px] font-medium uppercase tracking-wider" style={{ color: isToday ? "#1046a4" : "#94a3b8" }}>{wd}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Rows grouped by room_type */}
                        {layout.sections.map((sec) => {
                            const catColor = sec.color || "#94a3b8";
                            const isCollapsed = sec.collapsed;
                            return (
                            <div key={sec.key} className="relative" data-section-key={sec.key} data-collapsed={isCollapsed}>
                                {/* HORIZONTAL section header — sticky-top just below the date row */}
                                <div
                                    className="sticky z-[3] grid cursor-pointer select-none border-b border-[#e8ebf0] bg-card hover:bg-muted/40"
                                    style={{
                                        top: GRID_HEADER_HEIGHT + 24,
                                        gridTemplateColumns: `${ROOM_COL_WIDTH}px repeat(${days}, ${DAY_WIDTH}px)`,
                                        height: SECTION_HEADER_HEIGHT,
                                    }}
                                    onClick={() => toggleSection(sec.key)}
                                    data-testid={`section-toggle-${sec.key}`}
                                    title={isCollapsed ? `Hap — ${sec.label}` : `Palos — ${sec.label}`}
                                >
                                    {/* Sticky left label cell */}
                                    <div
                                        className="sticky left-0 z-[1] flex items-center gap-2 border-r border-[#e8ebf0] bg-card px-2.5"
                                        style={{ boxShadow: `inset 3px 0 0 ${catColor}` }}
                                    >
                                        <ChevronDown
                                            size={13}
                                            className="shrink-0 text-[#475569] transition-transform"
                                            style={{ transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}
                                        />
                                        <span className="truncate text-[12px] font-semibold text-[#1e3c72]" title={sec.label}>
                                            {sec.label}
                                        </span>
                                        <span
                                            className="ml-auto shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground dark:bg-slate-500/20 dark:text-slate-300"
                                        >
                                            {sec.rooms.length}
                                        </span>
                                    </div>
                                    {/* Empty date-cells row (kept for grid alignment) */}
                                    {dateRange.map((d) => {
                                        const ds = toYMD(d);
                                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                        const sum = summaryByKey[`${sec.key}|${ds}`];
                                        const avail = sum?.available_inventory;
                                        const rate = sum?.effective_rate;
                                        const curr = sum?.currency || "EUR";
                                        const overbooked = sum?.overbooked;
                                        const soldOut = sum?.sold_out;
                                        // Availability display: SOLD when 0, red when negative, else the number
                                        let availClass = "text-[13px] font-semibold leading-none text-[#1e3c72]";
                                        let availText;
                                        if (avail == null) {
                                            availText = "—";
                                            availClass = "text-[13px] font-medium leading-none text-slate-300";
                                        } else if (overbooked) {
                                            availText = String(avail);
                                            availClass = "text-[13px] font-bold leading-none text-red-600";
                                        } else if (soldOut) {
                                            availText = "SOLD";
                                            availClass = "text-[10px] font-bold uppercase tracking-wider leading-none text-red-500";
                                        } else {
                                            availText = String(avail);
                                        }
                                        const currencySym = { EUR: "€", USD: "$", GBP: "£", ALL: "L" }[curr] || "";
                                        const hasCTA = !!sum?.cta;
                                        const hasCTD = !!sum?.ctd;
                                        const minStay = sum?.min_stay || null;
                                        const stopSell = !!sum?.stop_sell;
                                        const hasRestriction = hasCTA || hasCTD || minStay || stopSell;
                                        const restrictionTitle = [
                                            stopSell ? "STOP · shitja e mbyllur" : null,
                                            hasCTA ? "CTA · nuk pranon check-in" : null,
                                            hasCTD ? "CTD · nuk pranon check-out" : null,
                                            minStay ? `Min qëndrimi ${minStay} netë` : null,
                                        ].filter(Boolean).join(" · ");
                                        return (
                                            <div
                                                key={ds}
                                                className={`relative flex flex-col items-center justify-center gap-0.5 border-l border-[#e8ebf0] ${isWeekend ? "bg-[#f8fafc]" : ""} ${canManageRates && rate != null ? "cursor-pointer hover:bg-[#fff7ed]" : ""}`}
                                                title={sum ? `${avail}/${sum.total_inventory} të lira · ${rate ? currencySym + rate : "pa tarifë"}${restrictionTitle ? " · " + restrictionTitle : ""}${canManageRates ? " · kliko për tarifat" : ""}` : ""}
                                                data-testid={`rt-summary-${sec.key}-${ds}`}
                                                onClick={(e) => {
                                                    // Only open the popover when clicking on the actual summary cell
                                                    // (not when the section header toggle bubble fires).
                                                    if (!canManageRates || rate == null || !sum) return;
                                                    e.stopPropagation();
                                                    const r = e.currentTarget.getBoundingClientRect();
                                                    setRatePopover({
                                                        rt_id: sum.room_type_id,
                                                        rt_name: sec.key,
                                                        date: ds,
                                                        currency: curr,
                                                        currencySym,
                                                        rate,
                                                        avail: sum.available_inventory,
                                                        blocked: sum.blocked || 0,
                                                        cta: hasCTA,
                                                        ctd: hasCTD,
                                                        min_stay: minStay,
                                                        stop_sell: stopSell,
                                                        x: r.left + r.width / 2,
                                                        y: r.bottom + 6,
                                                    });
                                                }}
                                            >
                                                <span className={availClass}>{availText}</span>
                                                <span className="text-[10px] font-medium leading-none text-muted-foreground">
                                                    {rate != null ? `${currencySym}${Math.round(rate)}` : "—"}
                                                </span>
                                                {hasRestriction && (
                                                    <div className="absolute right-0.5 top-0.5 flex items-center gap-[2px]" data-testid={`rt-restrictions-${sec.key}-${ds}`}>
                                                        {stopSell && (
                                                            <span className="rounded-sm bg-red-600 px-1 text-[8px] font-bold leading-[13px] text-white shadow-sm" title="STOP · shitja e mbyllur">S</span>
                                                        )}
                                                        {hasCTA && !stopSell && (
                                                            <span className="rounded-sm bg-amber-500 px-1 text-[8px] font-bold leading-[13px] text-white shadow-sm" title="CTA · nuk pranon check-in">A</span>
                                                        )}
                                                        {hasCTD && !stopSell && (
                                                            <span className="rounded-sm bg-purple-600 px-1 text-[8px] font-bold leading-[13px] text-white shadow-sm" title="CTD · nuk pranon check-out">D</span>
                                                        )}
                                                        {minStay && !stopSell && (
                                                            <span className="rounded-sm bg-slate-700 px-1 text-[8px] font-bold leading-[13px] text-white shadow-sm" title={`Min qëndrimi ${minStay} netë`}>{minStay}n</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Rooms inside section — hidden when collapsed */}
                                {!isCollapsed && sec.rooms.map((room) => (
                                    <div
                                        key={room.id}
                                        className="group grid border-b border-[#e8ebf0] last:border-0"
                                        style={{ gridTemplateColumns: `${ROOM_COL_WIDTH}px repeat(${days}, ${DAY_WIDTH}px)`, height: ROW_HEIGHT }}
                                        data-room-id={room.id}
                                    >
                                        <div
                                            className="sticky left-0 z-[1] flex items-center gap-2.5 overflow-hidden border-r border-[#e8ebf0] bg-card px-3"
                                        >
                                            <RoomStatusPill status={room.status} />
                                            <div className="truncate text-[17px] font-semibold tabular-nums leading-none" style={{ color: "#1e3c72" }} title={room.number}>{room.number}</div>
                                            {canManageRates && (
                                                <button
                                                    className="ml-auto rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-muted-foreground group-hover:opacity-100"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setBlockModal({ mode: "create", defaultRoomId: room.id });
                                                    }}
                                                    title="Blloko dhomën për një periudhë"
                                                    data-testid={`block-room-${room.number}`}
                                                >
                                                    <Ban size={13} />
                                                </button>
                                            )}
                                        </div>
                                        {dateRange.map((d) => {
                                            const ds = toYMD(d);
                                            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                            return (
                                                <button
                                                    key={ds}
                                                    onClick={() => {
                                                        setSelected({
                                                            room_id: room.id, property_id: propertyId,
                                                            check_in: ds, check_out: toYMD(addDays(d, 1)),
                                                        });
                                                        setOpenDrawer(true);
                                                    }}
                                                    className={`border-l border-[#e8ebf0] ${isWeekend ? "bg-[#f8fafc]" : ""} hover:bg-[#e8ebf0]/40`}
                                                    data-testid={`cell-${room.number}-${ds}`}
                                                />
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                            );
                        })}

                        {/* Reservation bars overlay — positioned via layout.yTop */}
                        {reservations.map((r) => {
                            const topPx = layout.yTop[r.room_id];
                            if (topPx == null) return null;
                            return (
                                <ReservationBar
                                    key={r.id}
                                    res={r}
                                    topPx={topPx}
                                    anchor={anchor}
                                    days={days}
                                    dragState={dragState}
                                    onStartDrag={startDrag}
                                    onClick={() => { setSelected(r); setOpenDrawer(true); }}
                                />
                            );
                        })}

                        {/* Room-block bars overlay — striped bars for maintenance / OOO / owner use */}
                        {roomBlocks.map((b) => {
                            const topPx = layout.yTop[b.room_id];
                            if (topPx == null) return null;
                            return (
                                <BlockBar
                                    key={b.id}
                                    block={b}
                                    topPx={topPx}
                                    anchor={anchor}
                                    days={days}
                                    canManage={canManageRates}
                                    onClick={() => setBlockModal({ mode: "edit", block: b })}
                                />
                            );
                        })}
                    </div>
                </div>
                {/* Reservation-status legend — bottom strip inside the page (below the calendar) */}
                <div className="mt-2 flex flex-wrap justify-start gap-2 px-1 pb-2">
                    <Legend />
                </div>
            </div>

            {openDrawer && (
                <ReservationDrawer
                    initial={selected}
                    rooms={rooms}
                    propertyId={propertyId}
                    onClose={() => setOpenDrawer(false)}
                    onSaved={async () => { setOpenDrawer(false); await load(); }}
                />
            )}

            {openGroupDrawer && (
                <GroupReservationDrawer
                    rooms={rooms}
                    propertyId={propertyId}
                    anchor={anchor}
                    onClose={() => setOpenGroupDrawer(false)}
                    onSaved={async () => { setOpenGroupDrawer(false); await load(); }}
                />
            )}

            {bulkBlockOpen && (
                <BulkBlockDrawer
                    rooms={rooms}
                    propertyId={propertyId}
                    anchor={anchor}
                    onClose={() => setBulkBlockOpen(false)}
                    onSaved={async () => { setBulkBlockOpen(false); await load(); }}
                />
            )}

            {ratePopover && (
                <RateCellPopover
                    data={ratePopover}
                    onClose={() => setRatePopover(null)}
                    onManage={() => { setRatePopover(null); navigate("/rate-plans"); }}
                />
            )}

            {blockModal && (
                <BlockDrawer
                    initial={blockModal}
                    rooms={rooms}
                    propertyId={propertyId}
                    onClose={() => setBlockModal(null)}
                    onSaved={async () => { setBlockModal(null); await load(); }}
                />
            )}
        </>
    );
}

function Legend() {
    const { t } = useTranslation();
    const items = [
        { label: t("reservations.legend.confirmed"), color: STATUS_COLOR.confirmed },
        { label: t("reservations.legend.checked_in"), color: STATUS_COLOR.checked_in },
        { label: t("reservations.legend.pending"), color: STATUS_COLOR.pending },
        { label: t("reservations.legend.checked_out"), color: STATUS_COLOR.checked_out },
        { label: t("reservations.legend.cancelled"), color: STATUS_COLOR.cancelled, striped: true },
        { label: t("reservations.legend.no_show"), color: STATUS_COLOR.no_show, striped: true },
        { label: "Bllokuar (mirëmbajtje)", color: "#64748b", striped: true, blockStripe: true },
    ];
    return (
        <div
            className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground"
            data-testid="cal-legend"
        >
            {items.map((it) => (
                <span key={it.label} className="inline-flex items-center gap-1.5">
                    <span
                        className="h-2.5 w-2.5 rounded-sm"
                        style={{
                            background: it.blockStripe
                                ? `repeating-linear-gradient(45deg, ${it.color}, ${it.color} 3px, #cbd5e1 3px, #cbd5e1 5px)`
                                : it.striped
                                    ? `repeating-linear-gradient(45deg, ${it.color}, ${it.color} 2px, rgba(255,255,255,0.55) 2px, rgba(255,255,255,0.55) 3.5px)`
                                    : it.color,
                        }}
                    />
                    {it.label}
                </span>
            ))}
            <span className="mx-1 h-3 w-px bg-border" />
            {/* Restriction badges */}
            <span className="inline-flex items-center gap-1" title="Shitja e mbyllur">
                <span className="rounded-sm bg-red-600 px-1 text-[8px] font-bold leading-[13px] text-white">S</span> STOP
            </span>
            <span className="inline-flex items-center gap-1" title="Closed to Arrival">
                <span className="rounded-sm bg-amber-500 px-1 text-[8px] font-bold leading-[13px] text-white">A</span> CTA
            </span>
            <span className="inline-flex items-center gap-1" title="Closed to Departure">
                <span className="rounded-sm bg-purple-600 px-1 text-[8px] font-bold leading-[13px] text-white">D</span> CTD
            </span>
            <span className="inline-flex items-center gap-1" title="Qëndrim minimal">
                <span className="rounded-sm bg-slate-700 px-1 text-[8px] font-bold leading-[13px] text-white">3n</span> min stay
            </span>
        </div>
    );
}

// Compact dropdown that lets reception filter the room-grid by housekeeping
// status ("free/clean", "in-cleaning", "occupied", "maintenance", …). Numbers
// beside each option reflect the current live count so staff can see at a
// glance how many rooms are in each bucket.
function RoomStatusFilter({ rooms, value, onChange }) {
    const { t } = useTranslation();
    const counts = useMemo(() => {
        const c = { clean: 0, dirty: 0, occupied: 0, maintenance: 0, blocked: 0, out_of_order: 0 };
        for (const r of rooms || []) {
            if (c[r.status] != null) c[r.status] += 1;
        }
        return c;
    }, [rooms]);
    const opts = [
        { v: "clean",        label: t("rooms.status.clean",        { defaultValue: "E lirë / e pastër" }), dot: "#22c55e" },
        { v: "dirty",        label: t("rooms.status.dirty",        { defaultValue: "Në pastrim" }),        dot: "#f59e0b" },
        { v: "occupied",     label: t("rooms.status.occupied",     { defaultValue: "E zënë" }),            dot: "#1046a4" },
        { v: "maintenance",  label: t("rooms.status.maintenance",  { defaultValue: "Mirëmbajtje" }),       dot: "#dc2626" },
        { v: "blocked",      label: t("rooms.status.blocked",      { defaultValue: "E bllokuar" }),        dot: "#64748b" },
        { v: "out_of_order", label: t("rooms.status.out_of_order", { defaultValue: "Jashtë përdorimi" }),  dot: "#7f1d1d" },
    ];
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="akm-input h-8 min-w-[220px] pr-3 text-[13px]"
                data-testid="room-status-filter"
                title="Filtro sipas statusit të dhomës"
            >
                <option value="">Të gjitha dhomat ({rooms.length})</option>
                {opts.map((o) => (
                    <option key={o.v} value={o.v}>{o.label} ({counts[o.v]})</option>
                ))}
            </select>
        </div>
    );
}

function ReservationBar({ res, topPx, anchor, days, dragState, onStartDrag, onClick }) {
    const [hover, setHover] = useState(false);
    const [tipCoords, setTipCoords] = useState(null);
    const barRef = useRef(null);
    const startMs = anchor.getTime();
    let ci = parseYMDLocal(res.check_in)?.getTime() ?? new Date(res.check_in).getTime();
    let co = parseYMDLocal(res.check_out)?.getTime() ?? new Date(res.check_out).getTime();
    let topOffset = 0;

    // Apply drag preview
    if (dragState && dragState.resId === res.id) {
        const dDays = dragState.deltaDays;
        if (dragState.mode === "move") {
            ci += dDays * 86400000;
            co += dDays * 86400000;
            // Approximate vertical preview — full row height per deltaRows.
            topOffset = dragState.deltaRows * ROW_HEIGHT;
        } else if (dragState.mode === "resize-start") {
            ci += dDays * 86400000;
        } else if (dragState.mode === "resize-end") {
            co += dDays * 86400000;
        }
    }

    const startOffset = Math.max(0, Math.round((ci - startMs) / 86400000));
    const endOffset = Math.min(days, Math.round((co - startMs) / 86400000));
    const span = endOffset - startOffset;
    if (span <= 0 || startOffset >= days) return null;

    const left = CATEGORY_COL_WIDTH + ROOM_COL_WIDTH + startOffset * DAY_WIDTH + 4;
    const width = span * DAY_WIDTH - 8;
    const top = MONTH_BAND_HEIGHT + GRID_HEADER_HEIGHT + topPx + topOffset + 8;
    const color = STATUS_COLOR[res.status] || "#64748B";
    const striped = STATUS_STRIPES.has(res.status);
    // For cancelled/no_show we overlay a diagonal-stripe pattern so the bar
    // stays visually distinct from confirmed/checked_in even at a glance.
    const barBackground = striped
        ? `repeating-linear-gradient(45deg, ${color}, ${color} 6px, rgba(255,255,255,0.28) 6px, rgba(255,255,255,0.28) 10px)`
        : `linear-gradient(180deg, ${color}, ${color}dd)`;
    const source = res.source || "direct";
    const isDragging = dragState && dragState.resId === res.id;
    const paid = (res.paid_amount ?? 0) >= (res.total_amount ?? 0) && (res.total_amount ?? 0) > 0;
    const nights = nightsBetween(parseYMDLocal(res.check_in) || new Date(res.check_in), parseYMDLocal(res.check_out) || new Date(res.check_out));

    return (
        <div
            ref={barRef}
            className={`absolute z-[2] flex items-stretch overflow-visible text-white shadow-[0_2px_6px_rgba(30,60,114,0.18)] ${isDragging ? "opacity-80 ring-2 ring-primary" : ""} ${res.status === "cancelled" ? "line-through-bar" : ""}`}
            style={{
                left, top, width, height: ROW_HEIGHT - 14,
                background: barBackground,
                clipPath: TICKET_CLIP,
                WebkitClipPath: TICKET_CLIP,
                transition: isDragging ? "none" : "left 120ms, top 120ms, width 120ms",
            }}
            data-testid={`res-bar-${res.id}`}
            data-status={res.status}
            data-source={source}
            onMouseDown={(e) => onStartDrag(e, "move", res)}
            onMouseEnter={() => {
                setHover(true);
                if (barRef.current) {
                    const r = barRef.current.getBoundingClientRect();
                    setTipCoords({ left: r.left + r.width / 2, top: r.top });
                }
            }}
            onMouseLeave={() => { setHover(false); setTipCoords(null); }}
        >
            {/* Left resize handle — sits inside the slanted arrival edge */}
            <div
                className="w-3 shrink-0 cursor-ew-resize bg-white/15 hover:bg-white/35 dark:bg-white/10 dark:hover:bg-white/25"
                onMouseDown={(e) => onStartDrag(e, "resize-start", res)}
            />
            <button
                onClick={(e) => { e.stopPropagation(); if (!dragState) onClick(); }}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden pl-1.5 pr-1.5 text-left text-[10.5px] font-medium"
            >
                {/* Name stacked — first line = first name, second = last name.
                    This lets narrow 1-day bars still show both by wrapping. */}
                <div className="flex min-w-0 flex-1 flex-col justify-center leading-[1.1]">
                    <span className="truncate">{res.guest_first_name}</span>
                    {res.guest_last_name && (
                        <span className="truncate text-[9.5px] text-white/85">
                            {res.guest_last_name}
                        </span>
                    )}
                </div>
                {/* Right cluster: PAID pill + brand chip */}
                <div className="flex shrink-0 items-center gap-1">
                    {paid && (
                        <span className="rounded-sm bg-white/25 px-1 text-[8.5px] font-bold leading-none">
                            PAID
                        </span>
                    )}
                    <SourceChip source={source} />
                </div>
            </button>
            <div
                className="w-3 shrink-0 cursor-ew-resize bg-white/15 hover:bg-white/35 dark:bg-white/10 dark:hover:bg-white/25"
                onMouseDown={(e) => onStartDrag(e, "resize-end", res)}
            />
            {hover && !isDragging && tipCoords && createPortal(
                <div
                    className="pointer-events-none fixed z-[80] -translate-x-1/2 -translate-y-full"
                    style={{ left: tipCoords.left, top: tipCoords.top - 10 }}
                >
                    <ResBarTooltip res={res} nights={nights} />
                </div>,
                document.body
            )}
        </div>
    );
}

function ResBarTooltip({ res, nights }) {
    const { t } = useTranslation();
    const status = t(`dashboard.res_status_${res.status}`, {
        defaultValue: t(`reservations.legend.${res.status}`, {
            defaultValue: (res.status || "").replace(/_/g, " "),
        }),
    });
    const source = res.source || "direct";
    // Hotel-level defaults from Panorama Kruje policy — bookings themselves
    // don't store per-reservation times yet, so we show the property's
    // configured Check-in From / Check-out Until times.
    const checkInTime = res.check_in_time || "13:30";
    const checkOutTime = res.check_out_time || "11:00";
    return (
        <div
            className="min-w-[280px] rounded-xl px-3.5 py-3 text-[12px] text-white shadow-2xl ring-1 ring-black/10"
            style={{ background: "rgba(30, 60, 114, 0.94)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
            data-testid={`res-tooltip-${res.id}`}
        >
            <div className="mb-1.5 flex items-center gap-2 text-[13px] font-semibold tracking-tight">
                <SourceChip source={source} />
                {res.guest_first_name} {res.guest_last_name}
            </div>
            <div className="space-y-1 text-white/90">
                <div className="flex items-center gap-1.5">
                    <Moon size={11} className="shrink-0" style={{ color: "#e8ebf0" }} />
                    <span>{nights} {t("reservations.nights")} · {formatDate(res.check_in)} → {formatDate(res.check_out)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/80">
                    <CalIcon size={11} className="shrink-0" style={{ color: "#e8ebf0" }} />
                    <span>Check-in nga <b className="text-white">{checkInTime}</b> · Check-out deri <b className="text-white">{checkOutTime}</b></span>
                </div>
                <div className="flex items-center gap-1.5">
                    <BedDouble size={11} className="shrink-0" style={{ color: "#e8ebf0" }} />
                    <span>{res.adults} {t("reservations.adults").toLowerCase()}{res.children ? ` · ${res.children} ${t("reservations.children").toLowerCase()}` : ""}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Globe2 size={11} className="shrink-0" style={{ color: "#e8ebf0" }} />
                    <span>{SOURCE_LABEL[source] || source}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <CreditCard size={11} className="shrink-0" style={{ color: "#e8ebf0" }} />
                    <span>{status}</span>
                </div>
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-white/15 pt-2">
                <span className="text-[10px] uppercase tracking-wider text-white/70">{t("reservations.total_amount")}</span>
                <span className="text-[14px] font-semibold">{formatCurrency(res.total_amount || 0)}</span>
            </div>
            {/* arrow pointing down to bar */}
            <div
                className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45"
                style={{ background: "rgba(30, 60, 114, 0.94)" }}
            />
        </div>
    );
}

function ReservationDrawer({ initial, rooms, propertyId, onClose, onSaved }) {
    const { t } = useTranslation();
    const isEdit = !!initial?.id;
    const [form, setForm] = useState({
        property_id: propertyId,
        room_id: initial?.room_id || rooms[0]?.id || "",
        check_in: initial?.check_in || toYMD(new Date()),
        check_out: initial?.check_out || toYMD(addDays(new Date(), 1)),
        adults: initial?.adults ?? 1,
        children: initial?.children ?? 0,
        total_amount: initial?.total_amount ?? 0,
        paid_amount: initial?.paid_amount ?? 0,
        payment_status: initial?.payment_status || "pending",
        status: initial?.status || "confirmed",
        source: initial?.source || "direct",
        notes: initial?.notes || "",
    });
    // Guest state is owned by the picker; keep the compat fields the rest of
    // the drawer reads (edit-mode fills the summary directly).
    const [guestValue, setGuestValue] = useState(() => {
        if (isEdit && initial?.guest_id) {
            return {
                mode: "existing",
                guest_id: initial.guest_id,
                guest: {
                    id: initial.guest_id,
                    first_name: initial.guest_first_name,
                    last_name: initial.guest_last_name,
                    email: initial.guest_email,
                    phone: initial.guest_phone,
                },
                first_name: initial.guest_first_name,
                last_name: initial.guest_last_name,
                email: initial.guest_email || "",
                phone: initial.guest_phone || "",
            };
        }
        return { mode: "existing" };
    });
    const [saving, setSaving] = useState(false);
    const [quote, setQuote] = useState(null);  // {total, nights, min_stay_required, min_stay_ok, plan_name, breakdown, reason}
    const [quoteLoading, setQuoteLoading] = useState(false);

    // Auto-fetch quote whenever room/dates change (but not while user has manually edited total).
    const manualTotalRef = useRef(false);
    useEffect(() => {
        if (isEdit) return;                       // don't override edits
        if (!form.room_id || !form.check_in || !form.check_out) return;
        if (form.check_out <= form.check_in) return;
        setQuoteLoading(true);
        api.get(`/reservations/quote`, { params: { room_id: form.room_id, check_in: form.check_in, check_out: form.check_out } })
            .then(({ data }) => {
                setQuote(data);
                // Only auto-fill total when user hasn't manually typed a non-zero total.
                if (!manualTotalRef.current && data?.total > 0) {
                    setForm((f) => ({ ...f, total_amount: data.total }));
                }
            })
            .catch(() => setQuote(null))
            .finally(() => setQuoteLoading(false));
        // eslint-disable-next-line
    }, [form.room_id, form.check_in, form.check_out]);

    const minStayBlocked = !isEdit && quote && quote.min_stay_required > 1 && !quote.min_stay_ok;
    const noPriceBlocked = !isEdit && quote && quote.total <= 0 && !!quote.reason;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (minStayBlocked) {
            toast.error(t("reservations.min_stay_error", { defaultValue: `Ky plan tarifor kërkon një qëndrim minimal prej ${quote.min_stay_required} netësh.`, count: quote.min_stay_required }));
            return;
        }
        if (noPriceBlocked) {
            toast.error(quote.reason);
            return;
        }
        if (!isEdit && !isGuestPickerValid(guestValue)) {
            toast.error(t("guest_picker.required", "Zgjidh ose plotëso të dhënat e klientit"));
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/reservations/${initial.id}`, {
                    room_id: form.room_id, check_in: form.check_in, check_out: form.check_out,
                    adults: Number(form.adults), children: Number(form.children),
                    total_amount: Number(form.total_amount), paid_amount: Number(form.paid_amount),
                    payment_status: form.payment_status, status: form.status, notes: form.notes,
                });
                toast.success(t("reservations.updated_success"));
            } else {
                await api.post("/reservations", {
                    ...form,
                    ...toReservationGuestPayload(guestValue),
                    adults: Number(form.adults),
                    children: Number(form.children),
                    total_amount: Number(form.total_amount),
                    paid_amount: Number(form.paid_amount || 0),
                    payment_status: form.payment_status || "pending",
                });
                toast.success(t("reservations.created_success"));
            }
            onSaved();
        } catch (e2) {
            const detail = e2.response?.data?.detail;
            // Backend field-level errors — surface inline via toast for now.
            toast.error(formatApiErrorDetail(detail) || t("reservations.save_failed"));
        }
        finally { setSaving(false); }
    };

    const quickAction = async (status) => {
        try {
            await api.patch(`/reservations/${initial.id}`, { status });
            const label = t(`dashboard.res_status_${status}`, { defaultValue: status.replace(/_/g, " ") });
            toast.success(`${t("reservations.status")}: ${label}`);
            onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const remove = async () => {
        if (!isEdit) return;
        if (!window.confirm(t("reservations.delete_confirm"))) return;
        try { await api.delete(`/reservations/${initial.id}`); toast.success(t("reservations.delete_success")); onSaved(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const nights = nightsBetween(form.check_in, form.check_out);
    const balance = Math.max(0, Number(form.total_amount) - Number(form.paid_amount));

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[480px] flex-col bg-card shadow-xl" data-testid="reservation-drawer">
                <div className="flex h-14 items-center justify-between border-b border-border px-4">
                    <div>
                        <div className="text-sm font-semibold tracking-tight">{isEdit ? t("reservations.edit") : t("reservations.new")}</div>
                        {isEdit && (
                            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                <span className="font-mono">#{initial.id.slice(0, 8)}</span>
                                {initial.source !== "direct" && (
                                    <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 capitalize">
                                        <Globe2 size={9} />{initial.source}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted/60"><X size={14} /></button>
                </div>

                {/* Quick actions */}
                {isEdit && (
                    <div className="flex flex-wrap gap-1.5 border-b border-[#e8ebf0] bg-[#f8fafc] px-3 py-2.5">
                        {form.status !== "checked_in" && form.status !== "checked_out" && (
                            <button onClick={() => quickAction("checked_in")}
                                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:opacity-90"
                                    style={{ background: "#1046a4" }}>
                                <CalIcon size={12} /> {t("reservations.check_in")}
                            </button>
                        )}
                        {form.status === "checked_in" && (
                            <button onClick={() => quickAction("checked_out")}
                                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:opacity-90"
                                    style={{ background: "#1e3c72" }}>
                                <CalIcon size={12} /> {t("reservations.check_out")}
                            </button>
                        )}
                        {form.status !== "no_show" && form.status !== "cancelled" && (
                            <>
                                <button onClick={() => quickAction("no_show")}
                                        className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-800 hover:bg-amber-100">
                                    {t("reservations.no_show")}
                                </button>
                                <button onClick={() => quickAction("cancelled")}
                                        className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-700 hover:bg-red-100">
                                    {t("reservations.cancel_reservation")}
                                </button>
                            </>
                        )}
                        <button onClick={async () => {
                            try {
                                const { data } = await api.post(`/reservations/${initial.id}/checkin-link`, {});
                                const url = `${window.location.origin}/check-in/${data.token}`;
                                await navigator.clipboard.writeText(url);
                                toast.success(t("reservations.link_copied"));
                            } catch (e) { toast.error(t("reservations.link_failed")); }
                        }}
                                className="inline-flex items-center gap-1 rounded-md border border-[#1046a4]/30 bg-card px-2.5 py-1.5 text-[11px] font-semibold hover:bg-[#e8ebf0]/60 dark:hover:bg-muted"
                                style={{ color: "#1046a4" }}
                                data-testid="send-checkin-link">
                            <Globe2 size={12} /> {t("reservations.online_checkin_link")}
                        </button>
                        <a href={`/api/payments/reservations/${initial.id}/invoice.pdf`} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 rounded-md border border-[#1046a4]/30 bg-card px-2.5 py-1.5 text-[11px] font-semibold hover:bg-[#e8ebf0]/60 dark:hover:bg-muted"
                           style={{ color: "#1046a4" }}>
                            <FileText size={12} /> {t("common.actions").includes("Veprime") ? "Faturë PDF" : "Invoice PDF"}
                        </a>
                    </div>
                )}

                <form onSubmit={submit} className="flex-1 overflow-y-auto p-4 space-y-3">
                    {/* Keycard panel (only if hotel has door-lock integration enabled) */}
                    {isEdit && (
                        <KeycardPanel
                            reservationId={initial.id}
                            status={form.status}
                            cards={initial.key_cards}
                            legacySnr={initial.key_card_snr}
                            onChanged={onSaved}
                        />
                    )}
                    {/* Guest */}
                    <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <User size={11} /> {t("reservations.guest")}
                    </div>
                    <GuestPicker
                        value={guestValue}
                        onChange={setGuestValue}
                        disabled={isEdit}
                        testid="reservation-guest-picker"
                    />

                    {/* Stay */}
                    <div className="flex items-center gap-2 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <CalIcon size={11} /> {t("reservations.stay")}
                    </div>
                    <Field label={t("reservations.room")}>
                        <select className="akm-input" value={form.room_id} onChange={(e) => setForm({ ...form, room_id: e.target.value })}>
                            {rooms.map((r) => <option key={r.id} value={r.id}>{r.number} · {r.room_type}</option>)}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label={t("reservations.check_in")}><input type="date" className="akm-input" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} /></Field>
                        <Field label={t("reservations.check_out")}><input type="date" className="akm-input" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} /></Field>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <Field label={t("reservations.adults")}><input type="number" min="1" className="akm-input" value={form.adults} onChange={(e) => setForm({ ...form, adults: e.target.value })} /></Field>
                        <Field label={t("reservations.children")}><input type="number" min="0" className="akm-input" value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} /></Field>
                        <Field label={t("reservations.nights")}><div className="akm-input flex items-center text-muted-foreground">{nights}</div></Field>
                    </div>

                    {/* Payment */}
                    <div className="flex items-center gap-2 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <CreditCard size={11} /> {t("reservations.payment")}
                    </div>
                    {!isEdit && (minStayBlocked || noPriceBlocked) && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-[11.5px] text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300" data-testid="reservation-validation-error">
                            {minStayBlocked
                                ? t("reservations.min_stay_error", { defaultValue: `Ky plan tarifor kërkon një qëndrim minimal prej ${quote.min_stay_required} netësh.`, count: quote.min_stay_required })
                                : quote.reason}
                        </div>
                    )}
                    {!isEdit && quote && quote.rate_plan_id && quote.min_stay_ok && !manualTotalRef.current && (
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid="reservation-auto-price">
                            {t("reservations.auto_priced", { defaultValue: `Çmimi u llogarit automatikisht nga plani "${quote.plan_name}" (${quote.nights} netë).` })}
                        </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                        <Field label={t("reservations.total_amount")}><input type="number" step="0.01" className="akm-input" value={form.total_amount} onChange={(e) => { manualTotalRef.current = true; setForm({ ...form, total_amount: e.target.value }); }} data-testid="total-amount-input" /></Field>
                        <Field label={t("reservations.paid")}><input type="number" step="0.01" className="akm-input" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} /></Field>
                        <Field label={t("reservations.balance")}><div className={`akm-input flex items-center font-mono ${balance > 0 ? "text-amber-700" : "text-green-700"}`}>{formatCurrency(balance)}</div></Field>
                    </div>
                    {Number(form.total_amount) > 0 && nights > 0 && (
                        <div className="-mt-1 text-[11px] text-muted-foreground" data-testid="per-night-hint">
                            {t("reservations.per_night_effective", {
                                defaultValue: "Efektive për natë: {{rate}} × {{n}} netë",
                                rate: formatCurrency(Number(form.total_amount) / nights),
                                n: nights,
                            })}
                        </div>
                    )}
                    <Field label={t("reservations.payment_status")}>
                        <select className="akm-input" value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })}>
                            {["pending", "partial", "paid", "refunded"].map((s) => (
                                <option key={s} value={s}>{t(`reservations.payment.${s}`, { defaultValue: s })}</option>
                            ))}
                        </select>
                    </Field>
                    {isEdit && initial?.folio_items?.length > 0 && (
                        <div className="mt-2 rounded-lg border border-border bg-muted/40 p-3" data-testid="reservation-folio">
                            <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                <Receipt size={11} /> Folio breakdown
                                <span className="ml-auto rounded-full bg-card px-1.5 py-0.5 text-[10px] text-muted-foreground">{initial.folio_items.length}</span>
                            </div>
                            <ul className="divide-y divide-border text-[12px]">
                                {initial.folio_items.map((it, i) => (
                                    <li key={i} className="flex items-center justify-between py-1.5" data-testid={`folio-item-${i}`}>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate font-medium text-foreground">
                                                {it.description || it.name || it.type || "Charge"}
                                            </div>
                                            {(it.qty || it.date) && (
                                                <div className="text-[10.5px] text-muted-foreground">
                                                    {it.qty ? `× ${it.qty}` : ""}
                                                    {it.qty && it.date ? " · " : ""}
                                                    {it.date || ""}
                                                </div>
                                            )}
                                        </div>
                                        <div className="ml-3 tabular-nums text-foreground">
                                            {formatCurrency(Number(it.amount ?? it.total ?? 0))}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {isEdit && (
                        <>
                            <button type="button"
                                    onClick={async () => {
                                        try {
                                            const token = localStorage.getItem("akm_token");
                                            const url = `${api.defaults.baseURL}/payments/reservations/${initial.id}/invoice.pdf`;
                                            const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                                            if (!r.ok) throw new Error(`HTTP ${r.status}`);
                                            const blob = await r.blob();
                                            const a = document.createElement("a");
                                            a.href = URL.createObjectURL(blob);
                                            a.download = `Invoice-${initial.id.slice(0, 8).toUpperCase()}.pdf`;
                                            a.click();
                                        } catch (err) {
                                            toast.error(t("reservations.invoice_failed") || "Invoice download failed");
                                        }
                                    }}
                                    className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[12px] text-primary hover:bg-primary/5"
                                    data-testid={`invoice-download-${initial.id}`}>
                                <FileText size={11} /> Invoice INV-{(initial.invoice_number || initial.id.slice(0, 8)).toUpperCase()}
                            </button>
                            <button type="button"
                                    onClick={async () => {
                                        const to = window.prompt("Dërgo faturën në email:", initial.guest_email || "");
                                        if (!to) return;
                                        try {
                                            const { data } = await api.post(`/payments/reservations/${initial.id}/email-invoice`, { to });
                                            toast.success(`Fatura u dërgua në ${data.sent_to}`);
                                        } catch (err) {
                                            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Dërgimi dështoi");
                                        }
                                    }}
                                    className="ml-1 inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[12px] text-emerald-700 hover:bg-emerald-50"
                                    data-testid={`invoice-email-${initial.id}`}
                                    title="Dërgo faturën si PDF në email">
                                <Send size={11} /> Dërgo
                            </button>
                        </>
                    )}
                    {isEdit && initial?.group_id && (
                        <>
                            <button type="button"
                                    onClick={async () => {
                                        try {
                                            const token = localStorage.getItem("akm_token");
                                            const url = `${api.defaults.baseURL}/payments/reservations/group/${initial.group_id}/invoice.pdf`;
                                            const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                                            if (!r.ok) throw new Error(`HTTP ${r.status}`);
                                            const blob = await r.blob();
                                            const a = document.createElement("a");
                                            a.href = URL.createObjectURL(blob);
                                            a.download = `GroupInvoice-${initial.group_id.slice(0, 8).toUpperCase()}.pdf`;
                                            a.click();
                                            toast.success(`Faturë grup: ${initial.group_size || "shumë"} dhoma`);
                                        } catch (err) {
                                            toast.error("Fatura e grupit dështoi");
                                        }
                                    }}
                                    className="ml-2 inline-flex items-center gap-1 rounded-md border border-[#ff7b02]/40 bg-orange-50 px-2 py-1 text-[12px] font-semibold text-[#ff7b02] hover:bg-orange-100"
                                    data-testid={`group-invoice-${initial.group_id}`}
                                    title={`Faturë e vetme për të gjitha dhomat e grupit (${initial.group_size || "?"} dhoma)`}>
                                <UsersRound size={11} /> Faturë Grup
                                {initial.group_size && <span className="ml-0.5 rounded bg-card px-1 text-[9px] font-mono">{initial.group_size}</span>}
                            </button>
                            <button type="button"
                                    onClick={async () => {
                                        const to = window.prompt("Dërgo faturën e grupit në email:", initial.guest_email || "");
                                        if (!to) return;
                                        try {
                                            const { data } = await api.post(`/payments/reservations/group/${initial.group_id}/email-invoice`, { to });
                                            toast.success(`Fatura e grupit u dërgua në ${data.sent_to}`);
                                        } catch (err) {
                                            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Dërgimi dështoi");
                                        }
                                    }}
                                    className="ml-1 inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-100"
                                    data-testid={`group-invoice-email-${initial.group_id}`}>
                                <Send size={11} /> Dërgo grup
                            </button>
                        </>
                    )}

                    {/* Booking */}
                    <div className="flex items-center gap-2 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("reservations.booking")}</div>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label={t("reservations.status")}>
                            <select className="akm-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                {["pending", "confirmed", "checked_in", "checked_out", "cancelled", "no_show"].map((s) => (
                                    <option key={s} value={s}>{t(`dashboard.res_status_${s}`, { defaultValue: s })}</option>
                                ))}
                            </select>
                        </Field>
                        {!isEdit && (
                            <Field label={t("reservations.source")}>
                                <select className="akm-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                                    {["direct", "booking", "airbnb", "expedia", "ical"].map((s) => (
                                        <option key={s} value={s}>{SOURCE_LABEL[s] || s}</option>
                                    ))}
                                </select>
                            </Field>
                        )}
                    </div>
                    <Field label={t("common.notes")}>
                        <textarea className="akm-input h-20 py-2" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </Field>
                </form>
                <div className="flex items-center gap-2 border-t border-border p-3">
                    {isEdit && <button onClick={remove} className="akm-btn-ghost text-red-700">{t("common.delete")}</button>}
                    <div className="flex-1" />
                    <button onClick={onClose} className="akm-btn-ghost">{t("common.cancel")}</button>
                    <button onClick={submit} disabled={saving || minStayBlocked || noPriceBlocked} className="akm-btn-primary" data-testid="reservation-save">{saving ? t("common.saving") : t("common.save")}</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}


// ---------------------------------------------------------------------------
// BlockBar — striped tape-chart bar for room blocks (maintenance / OOO / …)
// ---------------------------------------------------------------------------
function BlockBar({ block, topPx, anchor, days, canManage, onClick }) {
    const [hover, setHover] = useState(false);
    const [tipCoords, setTipCoords] = useState(null);
    const barRef = useRef(null);
    const startMs = anchor.getTime();
    const from = parseYMDLocal(block.from_date)?.getTime() ?? new Date(block.from_date).getTime();
    const to = parseYMDLocal(block.to_date)?.getTime() ?? new Date(block.to_date).getTime();
    const startOffset = Math.max(0, Math.round((from - startMs) / 86400000));
    const endOffset = Math.min(days, Math.round((to - startMs) / 86400000));
    const span = endOffset - startOffset;
    if (span <= 0 || startOffset >= days) return null;

    const left = ROOM_COL_WIDTH + startOffset * DAY_WIDTH + 4;
    const width = span * DAY_WIDTH - 8;
    const top = MONTH_BAND_HEIGHT + GRID_HEADER_HEIGHT + topPx + 8;
    const meta = BLOCK_META[block.reason] || BLOCK_META.other;
    const Icon = meta.icon;
    const bg = `repeating-linear-gradient(45deg, ${meta.color}, ${meta.color} 4px, rgba(255,255,255,0.28) 4px, rgba(255,255,255,0.28) 8px)`;

    return (
        <div
            ref={barRef}
            className="absolute z-[2] flex items-center overflow-visible text-white shadow-[0_2px_6px_rgba(30,60,114,0.14)]"
            style={{
                left, top, width, height: ROW_HEIGHT - 14,
                background: bg,
                clipPath: TICKET_CLIP,
                WebkitClipPath: TICKET_CLIP,
                cursor: canManage ? "pointer" : "default",
            }}
            data-testid={`block-bar-${block.id}`}
            data-reason={block.reason}
            onClick={(e) => { if (canManage) { e.stopPropagation(); onClick?.(); } }}
            onMouseEnter={() => {
                setHover(true);
                if (barRef.current) {
                    const r = barRef.current.getBoundingClientRect();
                    setTipCoords({ left: r.left + r.width / 2, top: r.top });
                }
            }}
            onMouseLeave={() => { setHover(false); setTipCoords(null); }}
        >
            <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-3.5 pr-2 text-left text-[11px] font-medium">
                <Icon size={11} className="shrink-0" />
                <span className="truncate">{meta.label}</span>
            </div>
            {hover && tipCoords && createPortal(
                <div
                    className="pointer-events-none fixed z-[80] -translate-x-1/2 -translate-y-full"
                    style={{ left: tipCoords.left, top: tipCoords.top - 10 }}
                >
                    <div
                        className="min-w-[220px] rounded-xl px-3 py-2 text-[12px] text-white shadow-2xl ring-1 ring-black/10"
                        style={{ background: "rgba(30, 60, 114, 0.94)", backdropFilter: "blur(8px)" }}
                    >
                        <div className="mb-1 flex items-center gap-1.5 font-semibold">
                            <Icon size={12} /> {meta.label}
                        </div>
                        <div className="text-white/90">
                            {formatDate(block.from_date)} → {formatDate(block.to_date)}
                        </div>
                        {block.notes && (
                            <div className="mt-1 text-white/80 italic">{block.notes}</div>
                        )}
                        {canManage && (
                            <div className="mt-1.5 text-[10px] uppercase tracking-wider text-white/60">Kliko për të modifikuar</div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}


// ---------------------------------------------------------------------------
// RateCellPopover — appears when a manager clicks a rate cell in the header.
// Fetches applicable rate plans for the room type and shows the effective
// price plus a "Menaxho tarifat" button linking to the Rate Plans page.
// ---------------------------------------------------------------------------
function RateCellPopover({ data, onClose, onManage }) {
    const [plans, setPlans] = useState(null); // null=loading, []=none, [...]=list
    const [prices, setPrices] = useState({}); // { rate_plan_id: { price, currency, min_stay, override } }

    // Load applicable plans + effective price per plan on mount
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data: all } = await api.get("/rate-plans", { params: { active: true } });
                if (!alive) return;
                const applicable = (all || []).filter((p) => {
                    if (!p.room_type_ids || p.room_type_ids.length === 0) return true; // empty = all types
                    return p.room_type_ids.includes(data.rt_id);
                });
                setPlans(applicable);
                // Kick off price lookups in parallel — one per plan.
                const entries = await Promise.all(applicable.map(async (p) => {
                    try {
                        const { data: pr } = await api.get(`/rate-plans/${p.id}/price`, { params: { date_str: data.date } });
                        return [p.id, pr];
                    } catch { return [p.id, null]; }
                }));
                if (!alive) return;
                setPrices(Object.fromEntries(entries));
            } catch (e) {
                if (alive) setPlans([]);
            }
        })();
        return () => { alive = false; };
    }, [data.rt_id, data.date]);

    // Escape closes the popover
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Position: try to fit under the anchor, but flip if it would go off screen
    const WIDTH = 320;
    const HEIGHT_EST = 260;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    let left = Math.max(12, Math.min(vw - WIDTH - 12, data.x - WIDTH / 2));
    let top = data.y;
    if (top + HEIGHT_EST > vh - 12) top = Math.max(12, data.y - HEIGHT_EST - 20);

    const symOf = (c) => ({ EUR: "€", USD: "$", GBP: "£", ALL: "L" }[c] || "");

    return createPortal(
        <>
            <div className="fixed inset-0 z-[70]" onClick={onClose} />
            <div
                className="fixed z-[71] w-[320px] rounded-xl border border-border bg-card text-foreground shadow-2xl"
                style={{ left, top }}
                data-testid="rate-cell-popover"
            >
                <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
                    <div>
                        <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                            {data.rt_name}
                        </div>
                        <div className="text-[15px] font-semibold text-[#1e3c72]">
                            {formatDate(data.date)}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                            Tarifa efektive · <span className="font-semibold text-foreground">{data.currencySym}{Math.round(data.rate)}</span>
                            {data.blocked > 0 && (
                                <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{data.blocked} bllok</span>
                            )}
                        </div>
                        {(data.stop_sell || data.cta || data.ctd || data.min_stay) && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid="rate-popover-restrictions">
                                {data.stop_sell && (
                                    <span className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800" title="Shitja e mbyllur">
                                        <span className="h-1.5 w-1.5 rounded-full bg-red-600" /> STOP
                                    </span>
                                )}
                                {data.cta && !data.stop_sell && (
                                    <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" title="Closed to Arrival — nuk pranon check-in">
                                        <span className="h-1.5 w-1.5 rounded-full bg-amber-600" /> CTA
                                    </span>
                                )}
                                {data.ctd && !data.stop_sell && (
                                    <span className="inline-flex items-center gap-1 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-800" title="Closed to Departure — nuk pranon check-out">
                                        <span className="h-1.5 w-1.5 rounded-full bg-purple-600" /> CTD
                                    </span>
                                )}
                                {data.min_stay && (
                                    <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground" title={`Qëndrim minimal ${data.min_stay} netë`}>
                                        min {data.min_stay}n
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-muted-foreground"
                        onClick={onClose}
                    >
                        <X size={13} />
                    </button>
                </div>

                <div className="max-h-[260px] overflow-y-auto p-3">
                    {plans == null && (
                        <div className="py-4 text-center text-[12px] text-muted-foreground">Duke ngarkuar…</div>
                    )}
                    {plans && plans.length === 0 && (
                        <div className="py-4 text-center text-[12px] text-muted-foreground">
                            Asnjë plan tarifash për këtë kategori.
                        </div>
                    )}
                    {plans && plans.length > 0 && (
                        <ul className="space-y-1.5">
                            {plans.map((p) => {
                                const pr = prices[p.id];
                                const sym = symOf(pr?.currency || p.currency);
                                const hasOverride = pr?.override != null;
                                return (
                                    <li
                                        key={p.id}
                                        className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-2.5 py-2"
                                        data-testid={`rate-plan-row-${p.code}`}
                                    >
                                        <Tag size={12} className="mt-0.5 shrink-0 text-[#ff7b02]" />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline gap-1.5">
                                                <span className="truncate text-[12px] font-semibold text-foreground">{p.name}</span>
                                                <span className="rounded bg-background px-1 text-[9.5px] font-mono uppercase tracking-wider text-muted-foreground ring-1 ring-border">{p.code}</span>
                                            </div>
                                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10.5px] text-muted-foreground">
                                                <span>{p.board_type}</span>
                                                <span>·</span>
                                                <span>min {p.min_stay}n</span>
                                                {p.include_breakfast && <><span>·</span><span>me mëngjes</span></>}
                                                {!p.refundable && <><span>·</span><span className="text-red-600">pa rimbursim</span></>}
                                            </div>
                                            {hasOverride && (
                                                <div className="mt-0.5 text-[10px] text-amber-700">
                                                    Sezoni: {pr.override.label}
                                                </div>
                                            )}
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <div className="text-[13px] font-semibold text-foreground tabular-nums">
                                                {pr ? `${sym}${Math.round(pr.price)}` : `${sym}${Math.round(p.base_price)}`}
                                            </div>
                                            {hasOverride && pr.override.override_price != null && (
                                                <div className="text-[10px] text-muted-foreground line-through">
                                                    {sym}{Math.round(p.base_price)}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-border px-3 py-2.5">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {plans?.length ? `${plans.length} plan${plans.length !== 1 ? "e" : ""}` : ""}
                    </span>
                    <button
                        onClick={onManage}
                        className="inline-flex items-center gap-1 rounded-md bg-[#ff7b02] px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:brightness-95"
                        data-testid="rate-popover-manage"
                    >
                        Menaxho tarifat <ExternalLink size={11} />
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
}


// ---------------------------------------------------------------------------
// BlockDrawer — create or edit a room block (side drawer).
// ---------------------------------------------------------------------------
function BlockDrawer({ initial, rooms, propertyId, onClose, onSaved }) {
    const isEdit = initial.mode === "edit";
    const src = initial.block || {};
    const [form, setForm] = useState({
        property_id: propertyId,
        room_id: src.room_id || initial.defaultRoomId || rooms[0]?.id || "",
        from_date: src.from_date || initial.defaultDate || toYMD(new Date()),
        to_date: src.to_date || toYMD(addDays(new Date(), 2)),
        reason: src.reason || "maintenance",
        notes: src.notes || "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (form.to_date <= form.from_date) {
            toast.error("Data e mbarimit duhet të jetë pas datës së fillimit");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/room-blocks/${src.id}`, {
                    from_date: form.from_date,
                    to_date: form.to_date,
                    reason: form.reason,
                    notes: form.notes,
                });
                toast.success("Blloku u përditësua");
            } else {
                await api.post("/room-blocks", form);
                toast.success("Dhoma u bllokua");
            }
            onSaved();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Ruajtja dështoi");
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!isEdit) return;
        if (!window.confirm("Të fshihet ky bllok i dhomës?")) return;
        try {
            await api.delete(`/room-blocks/${src.id}`);
            toast.success("Blloku u hoq");
            onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fshirja dështoi"); }
    };

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[420px] flex-col bg-card shadow-xl" data-testid="block-drawer">
                <div className="flex h-14 items-center justify-between border-b border-border px-4">
                    <div>
                        <div className="text-sm font-semibold tracking-tight">
                            {isEdit ? "Modifiko bllokun" : "Blloko dhomën"}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            {isEdit ? `#${(src.id || "").slice(0, 8)}` : "E re · operacionale"}
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted/60"><X size={14} /></button>
                </div>

                <form onSubmit={submit} className="flex-1 space-y-3 overflow-y-auto p-4">
                    <Field label="Dhoma">
                        <select
                            className="akm-input"
                            value={form.room_id}
                            onChange={(e) => setForm({ ...form, room_id: e.target.value })}
                            disabled={isEdit}
                            data-testid="block-room-select"
                        >
                            {rooms.map((r) => <option key={r.id} value={r.id}>{r.number} · {r.room_type}</option>)}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Nga">
                            <input
                                type="date" className="akm-input"
                                value={form.from_date}
                                onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                                data-testid="block-from-date"
                            />
                        </Field>
                        <Field label="Deri">
                            <input
                                type="date" className="akm-input"
                                value={form.to_date}
                                onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                                data-testid="block-to-date"
                            />
                        </Field>
                    </div>
                    <Field label="Arsyeja">
                        <select
                            className="akm-input"
                            value={form.reason}
                            onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            data-testid="block-reason-select"
                        >
                            {Object.entries(BLOCK_META).map(([k, meta]) => (
                                <option key={k} value={k}>{meta.label}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Shënime (opsionale)">
                        <textarea
                            className="akm-input h-24 py-2"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="p.sh. Riparim kondicioneri, ndërrim mobiljesh…"
                            data-testid="block-notes"
                        />
                    </Field>
                </form>

                <div className="flex items-center gap-2 border-t border-border p-3">
                    {isEdit && (
                        <button onClick={remove} className="akm-btn-ghost text-red-700" data-testid="block-delete">
                            Fshi
                        </button>
                    )}
                    <div className="flex-1" />
                    <button onClick={onClose} className="akm-btn-ghost">Anulo</button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="akm-btn-primary"
                        data-testid="block-save"
                    >
                        {saving ? "Duke ruajtur…" : isEdit ? "Ruaj" : "Blloko"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// GroupReservationDrawer — book multiple rooms for the same guest & dates,
// producing a single Group ID + shared invoice.
// ---------------------------------------------------------------------------
function GroupReservationDrawer({ rooms, propertyId, anchor, onClose, onSaved }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        property_id: propertyId,
        check_in: toYMD(anchor),
        check_out: toYMD(addDays(anchor, 2)),
        adults: 2,
        children: 0,
        total_amount: 0,
        status: "confirmed",
        source: "direct",
        notes: "",
    });
    const [guestValue, setGuestValue] = useState({ mode: "existing" });
    const [pickedRoomIds, setPickedRoomIds] = useState(new Set());
    const [filter, setFilter] = useState("");
    const [saving, setSaving] = useState(false);

    // Group rooms by room_type for the multi-select UI
    const grouped = useMemo(() => {
        const g = {};
        for (const r of rooms) {
            const key = r.room_type || "—";
            if (!g[key]) g[key] = [];
            if (!filter || String(r.number).includes(filter) || key.toLowerCase().includes(filter.toLowerCase())) {
                g[key].push(r);
            }
        }
        return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
    }, [rooms, filter]);

    const toggleRoom = (id) => {
        setPickedRoomIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const nights = useMemo(() => {
        try { return nightsBetween(form.check_in, form.check_out); } catch { return 0; }
    }, [form.check_in, form.check_out]);

    const perRoom = pickedRoomIds.size > 0 && Number(form.total_amount) > 0
        ? Math.round((Number(form.total_amount) / pickedRoomIds.size) * 100) / 100
        : 0;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (pickedRoomIds.size === 0) {
            toast.error(t("reservations.pick_at_least_one_room", "Zgjidh të paktën 1 dhomë"));
            return;
        }
        if (!isGuestPickerValid(guestValue)) {
            toast.error(t("guest_picker.required", "Zgjidh ose plotëso të dhënat e klientit"));
            return;
        }
        if (form.check_out <= form.check_in) {
            toast.error(t("reservations.checkout_must_be_after"));
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                ...toReservationGuestPayload(guestValue),
                room_ids: Array.from(pickedRoomIds),
                adults: Number(form.adults),
                children: Number(form.children),
                total_amount: Number(form.total_amount) || 0,
            };
            if (!payload.notes) delete payload.notes;
            const created = await api.post("/reservations/multi", payload);
            const groupId = created.data?.[0]?.group_id;
            toast.success(`${t("reservations.group_created", "Grupi u krijua")} · ${pickedRoomIds.size} ${t("reservations.rooms_word", "dhoma")} · ID ${(groupId || "").slice(0, 8)}`);
            onSaved();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || t("reservations.save_failed"));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[560px] flex-col bg-card shadow-xl" data-testid="group-reservation-drawer">
                <div className="flex h-14 items-center justify-between border-b border-border px-4">
                    <div>
                        <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
                            <UsersRound size={14} className="text-[#ff7b02]" /> Rezervim grup
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            Shumë dhoma · një klient · faturë e vetme
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted/60"><X size={14} /></button>
                </div>

                <form onSubmit={submit} noValidate className="flex-1 space-y-3 overflow-y-auto p-4">
                    {/* Guest */}
                    <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <User size={11} /> {t("reservations.primary_guest", "Klienti kryesor")}
                    </div>
                    <GuestPicker
                        value={guestValue}
                        onChange={setGuestValue}
                        testid="group-guest-picker"
                    />

                    {/* Dates */}
                    <div className="flex items-center gap-2 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <CalIcon size={11} /> Qëndrimi
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <Field label="Hyrja">
                            <input type="date" className="akm-input" value={form.check_in}
                                   onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                                   data-testid="group-check-in" />
                        </Field>
                        <Field label="Dalja">
                            <input type="date" className="akm-input" value={form.check_out}
                                   onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                                   data-testid="group-check-out" />
                        </Field>
                        <Field label="Netë">
                            <div className="akm-input flex items-center font-mono text-muted-foreground">{nights}</div>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Të rritur / dhomë">
                            <input type="number" min="1" className="akm-input" value={form.adults}
                                   onChange={(e) => setForm({ ...form, adults: e.target.value })} />
                        </Field>
                        <Field label="Fëmijë / dhomë">
                            <input type="number" min="0" className="akm-input" value={form.children}
                                   onChange={(e) => setForm({ ...form, children: e.target.value })} />
                        </Field>
                    </div>

                    {/* Rooms multi-picker */}
                    <div className="flex items-center gap-2 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <BedDouble size={11} /> Dhomat
                        <span className="ml-auto rounded-full bg-[#ff7b02]/10 px-2 py-0.5 text-[10px] font-semibold text-[#ff7b02]" data-testid="group-picked-count">
                            {pickedRoomIds.size} të zgjedhura
                        </span>
                    </div>
                    <input
                        className="akm-input"
                        placeholder="Kërko dhomë (numër ose kategori)…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        data-testid="group-room-search"
                    />
                    <div className="max-h-[260px] space-y-2 overflow-y-auto rounded-lg border border-border bg-muted/40 p-2">
                        {grouped.map(([type, rlist]) => (
                            <div key={type}>
                                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{type} · {rlist.length}</div>
                                <div className="flex flex-wrap gap-1">
                                    {rlist.map((r) => {
                                        const picked = pickedRoomIds.has(r.id);
                                        return (
                                            <button
                                                key={r.id}
                                                type="button"
                                                onClick={() => toggleRoom(r.id)}
                                                className={`rounded-md border px-2 py-1 text-[11.5px] font-semibold tabular-nums transition ${picked
                                                    ? "border-[#ff7b02] bg-[#ff7b02] text-white shadow-sm"
                                                    : "border-border bg-card text-foreground hover:border-[#ff7b02]/40 hover:bg-orange-50 dark:hover:bg-orange-500/10"}`}
                                                data-testid={`group-room-pick-${r.number}`}
                                            >
                                                {r.number}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Payment */}
                    <div className="flex items-center gap-2 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <CreditCard size={11} /> Pagesa
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Totali (të gjitha dhomat)">
                            <input type="number" step="0.01" className="akm-input" value={form.total_amount}
                                   onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                                   data-testid="group-total-amount" />
                        </Field>
                        <Field label="Për dhomë (auto)">
                            <div className="akm-input flex items-center font-mono text-muted-foreground">
                                {perRoom ? formatCurrency(perRoom) : "—"}
                            </div>
                        </Field>
                    </div>

                    {/* Booking */}
                    <div className="grid grid-cols-2 gap-2 pt-2">
                        <Field label="Statusi">
                            <select className="akm-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                {["pending", "confirmed"].map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="Burimi">
                            <select className="akm-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                                {["direct", "booking", "airbnb", "expedia"].map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Shënime">
                        <textarea className="akm-input h-16 py-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                  placeholder="p.sh. familje me 3 fëmijë, kërkohet dhomë ngjitur…" />
                    </Field>
                </form>

                <div className="flex items-center gap-2 border-t border-border p-3">
                    <div className="flex-1 text-[11px] text-muted-foreground">
                        {pickedRoomIds.size > 0 && (
                            <>Grupi krijon <b>{pickedRoomIds.size}</b> rezervime të lidhura + <b>1 faturë</b> të vetme</>
                        )}
                    </div>
                    <button onClick={onClose} className="akm-btn-ghost">Anulo</button>
                    <button
                        onClick={submit}
                        disabled={saving || pickedRoomIds.size === 0}
                        className="akm-btn-primary"
                        data-testid="group-save"
                    >
                        {saving ? "Duke krijuar…" : `Krijo grupin (${pickedRoomIds.size})`}
                    </button>
                </div>
            </div>
        </div>
    );
}




// ---------------------------------------------------------------------------
// BulkBlockDrawer — block MANY rooms at once (seasonal closure / renovation).
// ---------------------------------------------------------------------------
function BulkBlockDrawer({ rooms, propertyId, anchor, onClose, onSaved }) {
    const [form, setForm] = useState({
        from_date: toYMD(anchor),
        to_date: toYMD(addDays(anchor, 7)),
        reason: "maintenance",
        notes: "",
    });
    const [pickedRoomIds, setPickedRoomIds] = useState(new Set());
    const [filter, setFilter] = useState("");
    const [saving, setSaving] = useState(false);

    const grouped = useMemo(() => {
        const g = {};
        for (const r of rooms) {
            const key = r.room_type || "—";
            if (!g[key]) g[key] = [];
            if (!filter || String(r.number).includes(filter) || key.toLowerCase().includes(filter.toLowerCase())) {
                g[key].push(r);
            }
        }
        return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
    }, [rooms, filter]);

    const toggleRoom = (id) => {
        setPickedRoomIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectCategory = (rlist) => {
        setPickedRoomIds((prev) => {
            const next = new Set(prev);
            const allSelected = rlist.every((r) => next.has(r.id));
            for (const r of rlist) allSelected ? next.delete(r.id) : next.add(r.id);
            return next;
        });
    };
    const selectAll = () => setPickedRoomIds(new Set(rooms.map((r) => r.id)));
    const clearAll = () => setPickedRoomIds(new Set());

    const submit = async () => {
        if (pickedRoomIds.size === 0) { toast.error("Zgjidh të paktën 1 dhomë"); return; }
        if (form.to_date <= form.from_date) { toast.error("Data e mbarimit pas fillimit"); return; }
        setSaving(true);
        try {
            const res = await api.post("/room-blocks/bulk", {
                property_id: propertyId,
                room_ids: Array.from(pickedRoomIds),
                from_date: form.from_date,
                to_date: form.to_date,
                reason: form.reason,
                notes: form.notes,
            });
            const created = Array.isArray(res.data) ? res.data.length : 0;
            const skipped = Number(res.headers?.["x-bulk-skipped"] || 0);
            if (skipped > 0) {
                toast.warning(`${created} u bllokuan · ${skipped} u anashkaluan (konflikt me rezervime)`);
            } else {
                toast.success(`${created} dhoma u bllokuan për ${form.from_date} → ${form.to_date}`);
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bllokimi dështoi");
        } finally { setSaving(false); }
    };

    const nights = nightsBetween(form.from_date, form.to_date);

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[560px] flex-col bg-card shadow-xl" data-testid="bulk-block-drawer">
                <div className="flex h-14 items-center justify-between border-b border-border px-4">
                    <div>
                        <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
                            <Ban size={14} className="text-red-600" /> Blloko shumë dhoma
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            Mbyllje sezonale · mirëmbajtje · renovim
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted/60"><X size={14} /></button>
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto p-4">
                    <div className="grid grid-cols-3 gap-2">
                        <Field label="Nga"><input type="date" className="akm-input"
                                    value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                                    data-testid="bulk-from-date" /></Field>
                        <Field label="Deri"><input type="date" className="akm-input"
                                    value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                                    data-testid="bulk-to-date" /></Field>
                        <Field label="Netë">
                            <div className="akm-input flex items-center font-mono text-muted-foreground">{nights}</div>
                        </Field>
                    </div>
                    <Field label="Arsyeja">
                        <select className="akm-input" value={form.reason}
                                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                data-testid="bulk-reason">
                            {Object.entries(BLOCK_META).map(([k, meta]) => (
                                <option key={k} value={k}>{meta.label}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Shënime (opsionale)">
                        <textarea className="akm-input h-14 py-2" value={form.notes}
                                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                  placeholder="p.sh. Mbyllje dimërore për renovim..." />
                    </Field>

                    <div className="flex items-center gap-2 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        <BedDouble size={11} /> Dhomat për bllokim
                        <span className="ml-auto rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800" data-testid="bulk-picked-count">
                            {pickedRoomIds.size} të zgjedhura
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <input className="akm-input flex-1" placeholder="Kërko…"
                               value={filter} onChange={(e) => setFilter(e.target.value)}
                               data-testid="bulk-search" />
                        <button onClick={selectAll} className="rounded-md border border-border bg-card px-2 py-1 text-[11px] font-semibold hover:bg-muted" data-testid="bulk-select-all">
                            Të gjitha
                        </button>
                        <button onClick={clearAll} className="rounded-md border border-border bg-card px-2 py-1 text-[11px] font-semibold hover:bg-muted">
                            Pastro
                        </button>
                    </div>
                    <div className="max-h-[280px] space-y-2 overflow-y-auto rounded-lg border border-border bg-muted/40 p-2">
                        {grouped.map(([type, rlist]) => {
                            const allSelected = rlist.every((r) => pickedRoomIds.has(r.id));
                            const some = rlist.some((r) => pickedRoomIds.has(r.id));
                            return (
                                <div key={type}>
                                    <div className="mb-1 flex items-center gap-1.5">
                                        <button onClick={() => selectCategory(rlist)}
                                                className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition ${allSelected ? "bg-red-100 text-red-800" : some ? "bg-amber-50 text-amber-800" : "text-muted-foreground hover:bg-muted"}`}
                                                title="Zgjidh të gjithë këtë kategori">
                                            {type} · {rlist.length}
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {rlist.map((r) => {
                                            const picked = pickedRoomIds.has(r.id);
                                            return (
                                                <button key={r.id} type="button" onClick={() => toggleRoom(r.id)}
                                                        className={`rounded-md border px-2 py-1 text-[11.5px] font-semibold tabular-nums transition ${picked ? "border-red-600 bg-red-600 text-white shadow-sm" : "border-border bg-card text-foreground hover:border-red-400"}`}
                                                        data-testid={`bulk-room-${r.number}`}>
                                                    {r.number}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex items-center gap-2 border-t border-border p-3">
                    <div className="flex-1 text-[11px] text-muted-foreground">
                        {pickedRoomIds.size > 0 && (
                            <>Do të krijohen <b>{pickedRoomIds.size}</b> blloqe për <b>{nights}</b> netë</>
                        )}
                    </div>
                    <button onClick={onClose} className="akm-btn-ghost">Anulo</button>
                    <button onClick={submit} disabled={saving || pickedRoomIds.size === 0}
                            className="akm-btn-primary bg-red-600 hover:bg-red-700"
                            data-testid="bulk-save">
                        {saving ? "Duke bllokuar…" : `Blloko ${pickedRoomIds.size} dhoma`}
                    </button>
                </div>
            </div>
        </div>
    );
}
